"use client";
import { Button } from "@lattice/frontend/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@lattice/frontend/components/ui/dropdown-menu";
import { atom, useAtom } from "jotai";
import { Ellipsis } from "lucide-react";
import Link from "next/link";

import { routes } from "@lattice/frontend/lib/routes/routes";
import React from "react";

const active_section_atom = atom("");

export default function StockDashboardTabs({
  symbol,
  is_etf,
  is_crypto,
}: {
  symbol: string;
  is_etf: boolean;
  is_crypto: boolean;
}) {
  const [mounted, setMounted] = React.useState(false);
  const [active_section, setActiveSection] = useAtom(active_section_atom);
  const is_scrolling_ref = React.useRef(false);

  const tabs = React.useMemo(
    () => [
      { name: "Stats", link: "statistics", is_etf: false, is_crypto: false },
      { name: "Analysts", link: "analysts", is_etf: false, is_crypto: false },
      { name: "Sectors", link: "sectors", is_etf: true, is_crypto: false },
      {
        name: "Stock Make Up",
        link: "stock-make-up",
        is_etf: true,
        is_crypto: false,
      },
      { name: "News", link: "news", is_etf: null, is_crypto: true },
      {
        name: "Trading Trends",
        link: "trading-trends",
        is_etf: false,
        is_crypto: false,
      },
    ],
    [],
  );

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    if (!mounted) return;

    const intersectingSections = new Set<string>();

    const observerOptions = {
      rootMargin: "-100px 0px -60% 0px",
      threshold: [0, 0.1, 0.5, 1],
    };

    const observer = new IntersectionObserver((entries) => {
      if (is_scrolling_ref.current) return;

      entries.forEach((entry) => {
        const sectionId =
          entry.target.id || entry.target.querySelector("[id]")?.id;

        if (sectionId) {
          if (entry.isIntersecting) {
            intersectingSections.add(sectionId);
          } else {
            intersectingSections.delete(sectionId);
          }
        }
      });

      if (intersectingSections.size > 0) {
        for (const tab of tabs) {
          if (intersectingSections.has(tab.link)) {
            setActiveSection(tab.link);
            break;
          }
        }
      }
    }, observerOptions);

    setTimeout(() => {
      tabs.forEach((tab) => {
        const element =
          document.getElementById(tab.link) ||
          document.querySelector(`h2#${tab.link}`) ||
          document.querySelector(`[id="${tab.link}"]`);

        if (element) {
          observer.observe(element);
        }
      });
    }, 100);

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      if (scrollY < 100) {
        setActiveSection("analysts");
        return;
      }

      if (scrollY + windowHeight >= documentHeight - 100) {
        setActiveSection("insider-trades");
        return;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll);
    };
  }, [tabs, mounted, setActiveSection]);

  const filtered_tabs = tabs.filter(
    (t) =>
      t.is_etf === null || (t.is_etf === is_etf && t.is_crypto === is_crypto),
  );
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="outline"
            className="self-center inline-flex lg:hidden"
          >
            <Ellipsis />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {filtered_tabs.map((tab) => (
            <DropdownMenuItem
              key={tab.name}
              className={
                mounted && active_section === tab.link ? "font-semibold" : ""
              }
              onSelect={() => {
                is_scrolling_ref.current = true;
                setActiveSection(tab.link);

                setTimeout(() => {
                  const element =
                    document.getElementById(tab.link) ||
                    document.querySelector(`#${tab.link}`) ||
                    document.querySelector(`[id="${tab.link}"]`);

                  if (element) {
                    element.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });

                    setTimeout(() => {
                      window.scrollBy({ top: -150, behavior: "smooth" });
                    }, 100);
                  } else {
                    window.location.hash = tab.link;
                  }

                  setTimeout(() => {
                    is_scrolling_ref.current = false;
                  }, 1000);
                }, 10);
              }}
            >
              {tab.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <ul className="hidden items-center gap-6 overflow-x-auto lg:flex">
        {filtered_tabs.map((tab) => (
          <li key={tab.name}>
            <Link
              prefetch={false}
              href={`${routes.stock(symbol)}#${tab.link}`}
              id={`${tab.link}-link`}
              className={`text-base font-medium whitespace-nowrap transition-colors hover:text-foreground cursor-pointer ${
                mounted && active_section === tab.link
                  ? "text-primary font-semibold brightness-125"
                  : "text-muted-foreground"
              }`}
              onClick={(e) => {
                e.preventDefault();

                is_scrolling_ref.current = true;
                setActiveSection(tab.link);

                setTimeout(() => {
                  const element =
                    document.getElementById(tab.link) ||
                    document.querySelector(`#${tab.link}`) ||
                    document.querySelector(`[id="${tab.link}"]`);

                  if (element) {
                    element.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });

                    setTimeout(() => {
                      window.scrollBy({ top: -150, behavior: "smooth" });
                    }, 100);
                  } else {
                    window.location.hash = tab.link;
                  }

                  setTimeout(() => {
                    is_scrolling_ref.current = false;
                  }, 1000);
                }, 10);
              }}
            >
              {tab.name}
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}
